'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAppDispatch, useAppSelector } from 'lib/redux/hooks';
import { 
  selectAccessToken, 
  selectIsAuthenticationOpen,
  setIsAuthenticationOpen 
} from 'lib/redux/uiSlice';
import { isTokenExpired } from 'lib/utils/verifyJwt';
import { getUserProfile } from 'api/userApi';
import { getSubscriptionInfo } from 'api/subscription/subscriptionInfo';
import { AuthenticationOptions } from 'components/AuthenticationOptions';

type ConnectionStatus = 'checking' | 'waiting_auth' | 'authenticated' | 'sharing' | 'success' | 'error';

export default function ExtensionConnectPage() {
  const searchParams = useSearchParams();
  const reason = searchParams?.get('reason') || 'manual';
  const nonce = searchParams?.get('nonce');
  const extId = searchParams?.get('ext');
  
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);
  
  const [status, setStatus] = useState<ConnectionStatus>('checking');
  const [error, setError] = useState<string>('');
  const [extensionReady, setExtensionReady] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [securityError, setSecurityError] = useState<string>('');
  const extensionReadyRef = useRef(false);
  const statusRef = useRef<ConnectionStatus>('checking');
  const hasReloadedRef = useRef(false);
  
 // Use sessionStorage to prevent repeated reloads across tab focus/blur events
  useEffect(() => {
    // Check if we've already handled reload in this session
    const alreadyReloaded = sessionStorage.getItem('extension_connect_reloaded');
    if (hasReloadedRef.current || alreadyReloaded === 'true') {
      return;
    }
    
    const token = localStorage.getItem('access-token');
    const hasToken = token && !isTokenExpired(token);
    
    // Only reload if we have token but extension isn't ready
    // This typically happens after OAuth redirect with SPA navigation
    if (hasToken && !extensionReady) {
      const timer = setTimeout(() => {
        if (!extensionReadyRef.current && !hasReloadedRef.current) {
          // Mark that we're about to reload to prevent loops
          sessionStorage.setItem('extension_connect_reloaded', 'true');
          hasReloadedRef.current = true;
          window.location.reload();
        }
      }, 2000); // Give extension 2 seconds to respond
      
      return () => clearTimeout(timer);
    } else if (hasToken && extensionReady) {
      // If extension is ready, clear the reload flag for next time
      sessionStorage.removeItem('extension_connect_reloaded');
    }
  }, [extensionReady]);
  
  // Keep refs in sync with state
  useEffect(() => {
    extensionReadyRef.current = extensionReady;
  }, [extensionReady]);
  
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  
  // Store complete URL with params for OAuth redirect restoration
  useEffect(() => {
    if (nonce && extId) {
      // Store the complete URL with query params for OAuth flow
      const fullUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem('extension_connect_url', fullUrl);
    }
  }, [nonce, extId]);

  // Security check: Block manual access without valid handshake
  useEffect(() => {
    if (!nonce || !extId) {
      // Check if this is an OAuth redirect that lost parameters
      const storedUrl = sessionStorage.getItem('extension_connect_url');
      
      if (storedUrl && reason) {
        // This is an OAuth redirect - restore the original URL with params
        // console.log('[ExtConnect] 🔄 Restoring URL after OAuth redirect:', storedUrl);
        sessionStorage.removeItem('extension_connect_url');
        window.location.href = storedUrl;
        return;
      }
      
      // No stored URL - this is a manual access attempt
      setSecurityError('Invalid access: This page requires extension authentication');
      setStatus('error');
      // Redirect after 5 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 5000);
    }
  }, [nonce, extId, reason]);

  // Helper to add debug logs - memoized to prevent infinite loops
  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  // Listen for extension messages - only runs ONCE on mount
  useEffect(() => {
    // addLog('Initializing message listener...');
    
    const handleMessage = (event: MessageEvent) => {
      // Validate event data exists
      if (!event.data || typeof event.data !== 'object') {
        return; // Ignore invalid messages silently
      }

      const { type } = event.data;
      
      // Only process our extension messages
      if (!type || (type !== 'EXTENSION_READY' && type !== 'EXTENSION_AUTH_SUCCESS')) {
        return; // Ignore other messages
      }

      // Security: Accept messages from same origin (content script context)
      if (event.origin !== window.location.origin) {
        console.error('[Platform] 🚨 SECURITY: Rejected message from wrong origin', event.origin);
        return;
      }

      // Security: Validate nonce matches
      if (event.data.nonce !== nonce) {
        console.error('[Platform] 🚨 SECURITY: Nonce mismatch');
        return;
      }

      // Security: Validate extension ID matches
      if (event.data.extensionId !== extId) {
        console.error('[Platform] 🚨 SECURITY: Extension ID mismatch');
        return;
      }

      if (type === 'EXTENSION_READY') {
        setExtensionReady(true);
      }

      if (type === 'EXTENSION_AUTH_SUCCESS') {
        setStatus('success');
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Fallback: If no extension responds in 3 seconds, assume extension not installed
    const fallbackTimer = setTimeout(() => {
      if (!extensionReadyRef.current) {
        // addLog('⚠️ No extension detected after 3s, proceeding anyway');
        setExtensionReady(true);
      }
    }, 3000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallbackTimer);
    };
    // Empty deps - only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Define shareTokenWithExtension before the useEffect that uses it
  const shareTokenWithExtension = useCallback(async () => {
    try {
      setStatus('sharing');
      
      // Get token from localStorage (most reliable source)
      const token = localStorage.getItem('access-token');
      
      if (!token) {
        // addLog('❌ No token in localStorage');
        setStatus('waiting_auth');
        return;
      }

      // addLog('📤 Preparing to share token...');

      // Fetch user data for extension
      const [userProfile, subscriptionInfo] = await Promise.all([
        getUserProfile().catch((err) => {
          // addLog(`⚠️ Failed to fetch user profile: ${err.message}`);
          return null;
        }),
        getSubscriptionInfo().catch((err) => {
          // addLog(`⚠️ Failed to fetch subscription: ${err.message}`);
          return { premium: false, credits: 0 };
        })
      ]);

      // addLog('📤 Sending postMessage with token...');
      // addLog(`📡 Origin: ${window.location.origin}`);
      // addLog(`📦 Token length: ${token.length} chars`);

      // Send token to extension via postMessage with nonce validation
      const messageData = {
        type: 'RESUMEMATE_AUTH_TOKEN',
        token: token,
        nonce: nonce, // Include nonce for validation
        user: {
          id: userProfile?.id || '',
          email: userProfile?.email || '',
          name: userProfile?.name || '',
          isPremium: subscriptionInfo?.premium || false,
        }
      };
      
      // Send multiple times to handle any timing/listener issues (like EXTENSION_READY does)
      const sendTokenMessage = () => {
        window.postMessage(messageData, window.location.origin); // Use specific origin
      };
      
      // Send immediately and retry
      sendTokenMessage();
      setTimeout(sendTokenMessage, 100);
      setTimeout(sendTokenMessage, 300);
      setTimeout(sendTokenMessage, 500);

      // addLog('✅ postMessage sent! Waiting for extension confirmation...');
      // addLog('⏳ Extension has 3 seconds to respond...');

      // Status will be updated to 'success' when extension confirms receipt
      // If no confirmation in 3 seconds, check if extension is installed
      // Use ref to avoid stale closure
      setTimeout(() => {
        if (statusRef.current === 'sharing') {
          // addLog('⚠️ No extension response - possible issues:');
          // addLog('  1. Extension content script not loaded');
          // addLog('  2. Extension not installed');
          // addLog('  3. Content script URL pattern mismatch');
          setStatus('success'); // Proceed anyway for platform-only testing
        }
      }, 3000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      // addLog(`❌ Failed to share token: ${errorMsg}`);
      setError('Failed to connect extension. Please try again.');
      setStatus('error');
    }
  }, [addLog]); // addLog is stable, setStatus/setError are from useState (stable)

  // Main effect: Share token when both extension is ready AND we have a valid token
  // This runs after OAuth redirect, after sign-in overlay, or on page load if already authenticated
  useEffect(() => {
    const attemptTokenShare = async () => {
      // Don't attempt if already sharing or successful
      if (status === 'sharing' || status === 'success') {
        return;
      }

      // Check both Redux and localStorage for token
      const reduxToken = accessToken;
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('access-token') : null;
      const token = reduxToken || localToken;

      if (!token || isTokenExpired(token)) {
        // No valid token - user needs to sign in
        if (status !== 'waiting_auth') {
          // addLog('❌ No valid token - user needs to sign in');
          setStatus('waiting_auth');
        }
        return;
      }

      // We have valid token and extension is ready - share it!
      if (extensionReady) {
        // addLog('✅ Valid token + extension ready → Sharing token...');
        await shareTokenWithExtension();
      }
    };

    attemptTokenShare();
  }, [extensionReady, accessToken, status, addLog, shareTokenWithExtension]);

  // Note: Tab closing is handled by the extension via chrome.tabs.remove()
  // window.close() doesn't work for extension-opened tabs
  useEffect(() => {
    if (status === 'success') {
      // Notify extension to close this tab
      window.postMessage({ type: 'CLOSE_TAB_REQUEST' }, '*');
    }
  }, [status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        
        {/* Checking Status */}
        {status === 'checking' && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Initializing Connection...
            </h2>
            <p className="text-gray-600">
              Please wait while we prepare your extension
            </p>
          </div>
        )}

        {/* Waiting for Authentication */}
        {status === 'waiting_auth' && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Sign In Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please sign in to connect your ResumeMate extension
            </p>
            <button
              onClick={() => dispatch(setIsAuthenticationOpen(true))}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium"
            >
              Sign In to Continue
            </button>
            <p className="text-xs text-gray-500 mt-4">
              Reason: {reason === 'install' ? 'First-time setup' : reason === 'expired' ? 'Session expired' : 'Manual connection'}
            </p>
          </div>
        )}

        {/* Sharing Token */}
        {(status === 'authenticated' || status === 'sharing') && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connecting Extension...
            </h2>
            <p className="text-gray-600">
              Securely sharing your credentials
            </p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Successfully Connected!
            </h2>
            <p className="text-gray-600 mb-4">
              Your ResumeMate extension is now connected to your account
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>✨ You're all set!</strong>
                <br />
                Start tracking jobs on LinkedIn, Indeed, and more
              </p>
            </div>
            <p className="text-xs text-gray-500">
              This tab will close automatically...
            </p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {securityError ? 'Access Denied' : 'Connection Failed'}
            </h2>
            <p className="text-gray-600 mb-6">
              {securityError || error || 'Unable to connect extension. Please try again.'}
            </p>
            {securityError ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>🔒 Security Notice:</strong>
                  <br />
                  This page can only be accessed through the ResumeMate extension.
                  <br />
                  Redirecting to home page...
                </p>
              </div>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium"
              >
                Try Again
              </button>
            )}
          </div>
        )}

      </div>

      {/* Authentication Overlay (reuses existing component) */}
      {isAuthenticationOpen && (
        <AuthenticationOptions 
          redirectTo={nonce && extId ? `/extension/connect?nonce=${nonce}&ext=${extId}&reason=${reason}` : '/extension/connect'} 
        />
      )}

      {/* Debug Panel - Commented out for production */}
      {/*<div className="fixed bottom-4 right-4 max-w-md bg-gray-900 text-white rounded-lg shadow-xl p-4 text-xs max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold text-sm">🐛 Debug Info</h3>
          <span className="text-green-400">{status}</span>
        </div>
        <div className="space-y-1 font-mono">
          <div>Extension Ready: {extensionReady ? '✅' : '⏳'}</div>
          <div>Token Exists: {typeof window !== 'undefined' && localStorage.getItem('access-token') ? '✅' : '❌'}</div>
          <div>Reason: {reason}</div>
          <hr className="border-gray-700 my-2" />
          <div className="text-xs text-gray-400">Recent Logs:</div>
          {debugLogs.slice(-10).map((log, i) => (
            <div key={i} className="text-gray-300">{log}</div>
          ))}
        </div>
      </div> */}
     
    </div>
  );
}
